import * as React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { direction, Language } from "./i18n"
import { translateHtml } from "./translator"
import { sanitizeHtml } from "./sanitize"

type Ctx = {
  lang: Language
  setLang: (l: Language) => void
  isSwitching: boolean
  lastError: string | null
}

const LanguageContext = React.createContext<Ctx | null>(null)

const LANG_KEY = "app.lang"
const LANG_EXPIRES_KEY = "app.lang.expiresAt"
const LANG_TTL_MS = 180 * 24 * 60 * 60 * 1000

function readStoredLang(): Language | null {
  try {
    const saved = localStorage.getItem(LANG_KEY)
    if (saved !== "en" && saved !== "hi" && saved !== "pa") return null

    const expiresRaw = localStorage.getItem(LANG_EXPIRES_KEY)
    if (expiresRaw != null && expiresRaw !== "") {
      const expiresAt = Number(expiresRaw)
      if (!Number.isFinite(expiresAt) || expiresAt <= 0) {
        localStorage.removeItem(LANG_KEY)
        localStorage.removeItem(LANG_EXPIRES_KEY)
        return null
      }
      if (Date.now() > expiresAt) {
        localStorage.removeItem(LANG_KEY)
        localStorage.removeItem(LANG_EXPIRES_KEY)
        return null
      }
    }

    return saved
  } catch {
    return null
  }
}

function writeStoredLang(next: Language): boolean {
  try {
    localStorage.setItem(LANG_KEY, next)
    localStorage.setItem(LANG_EXPIRES_KEY, String(Date.now() + LANG_TTL_MS))
    return true
  } catch {
    return false
  }
}


// Translate opt-in DOM nodes outside React using data-auto-translate
async function applyAutoTranslate(lang: Language, setError: (e: string | null) => void) {
  try {
    const candidates = Array.from(document.querySelectorAll('[data-auto-translate="true"]'))
    await Promise.all(
      candidates.map(async (el) => {
        try {
          // Skip elements already marked as translated
          const already = el.getAttribute("data-translated")
          if (already === "true") return
          const html = el.innerHTML
          const safe = sanitizeHtml(html)
          const translated = lang === "en" ? safe : await translateHtml(safe, lang)
          el.innerHTML = translated
          el.setAttribute("data-translated", lang === "en" ? "false" : "true")
        } catch (e: any) {
          setError(e?.message || "DOM translation failed")
        }
      })
    )
  } catch (e: any) {
    setError(e?.message || "DOM translation outer failed")
  }
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize from localStorage or default to English
  const [lang, setLangState] = useState<Language>(() => {
    return readStoredLang() ?? "en"
  })
  const [isSwitching, setIsSwitching] = useState(false)
  const [lastError, setLastError] = useState<string | null>(null)
  const switchingTimer = useRef<number | null>(null)

  const setLang = useCallback((next: Language) => {
    if (next === lang) return
    try {
      console.info("[lang] setLang", { from: lang, to: next })
      writeStoredLang(next)
    } catch { }
    setIsSwitching(true)
    if (switchingTimer.current != null) {
      globalThis.clearTimeout(switchingTimer.current)
      switchingTimer.current = null
    }
    switchingTimer.current = globalThis.setTimeout(() => setIsSwitching(false), 200) as unknown as number
    setLangState(next)
  }, [lang])

  useEffect(() => {
    return () => {
      if (switchingTimer.current != null) {
        globalThis.clearTimeout(switchingTimer.current)
        switchingTimer.current = null
      }
    }
  }, [])

  useEffect(() => {
    document.documentElement.lang = lang
    document.documentElement.dir = direction(lang)
  }, [lang])

  // Dispatch a custom event for any non-React listeners
  useEffect(() => {
    try {
      const ev = new CustomEvent("app:languagechange", { detail: { lang } })
      if (typeof window !== 'undefined') window.dispatchEvent(ev)
      console.debug("[lang] event dispatched", { lang })
    } catch { }
  }, [lang])

  // Apply auto-translate to opt-in DOM nodes
  useEffect(() => {
    let active = true
    applyAutoTranslate(lang, (e) => {
      if (active) setLastError(e)
    }).catch((e) => active && setLastError(e?.message || String(e)))
    return () => { active = false }
  }, [lang])

  const value = useMemo<Ctx>(() => ({ lang, setLang, isSwitching, lastError }), [lang, setLang, isSwitching, lastError])
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const ctx = React.useContext(LanguageContext)
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider")
  return ctx
}
